import { useMemo, useState } from "react";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import type { MediationTimelineEvent, MediationTimelineKind } from "@expertloop/shared";
import { CollapsibleCard } from "./CollapsibleCard";
import { EVENT_STYLE } from "./conversation";

type Filter = "all" | "messages" | "deliveries" | "finance";

const FILTER_KINDS: Record<Exclude<Filter, "all">, MediationTimelineKind[]> = {
	messages: ["message"],
	deliveries: ["delivery"],
	finance: ["finance", "mediation"],
};

const FILTERS: Array<{ value: Filter; icon: string; label: MessageKey }> = [
	{ value: "all", icon: "fa-list", label: "adminChatDetail.dossier.timeline.all" },
	{ value: "messages", icon: "fa-comment", label: "adminChatDetail.dossier.timeline.messages" },
	{ value: "deliveries", icon: "fa-box", label: "adminChatDetail.dossier.timeline.deliveries" },
	{ value: "finance", icon: "fa-euro-sign", label: "adminChatDetail.dossier.timeline.finance" },
];

interface TimelineSection {
	key: string;
	title: { key: MessageKey; values?: Record<string, number> };
	events: MediationTimelineEvent[];
}

/**
 * Découpe la chronologie en phases, dans l'ordre des faits : ce qui précède la première livraison,
 * chaque livraison avec ce qui la suit, puis chaque tour de médiation avec sa décision. Elle
 * remplace l'ancien historique de livraison, que cette lecture rend redondant.
 */
export function buildSections(timeline: MediationTimelineEvent[]): TimelineSection[] {
	const sections: TimelineSection[] = [];
	let deliveryCount = 0;
	let current: TimelineSection = { key: "before", title: { key: "adminChatDetail.dossier.timeline.sectionBefore" }, events: [] };
	sections.push(current);

	const startSection = (section: TimelineSection) => {
		current = section;
		sections.push(section);
	};

	for (const event of timeline) {
		if (event.type === "delivery_published") {
			deliveryCount += 1;
			startSection({ key: `delivery-${deliveryCount}`, title: { key: "adminChatDetail.dossier.timeline.sectionDelivery", values: { number: deliveryCount } }, events: [] });
		} else if (event.type === "mediation_opened" || event.type === "mediation_reopened") {
			startSection({ key: `mediation-${event.roundNumber ?? sections.length}`, title: { key: "adminChatDetail.dossier.timeline.sectionMediation", values: { number: event.roundNumber ?? 1 } }, events: [] });
		}
		current.events.push(event);
	}
	return sections.filter((section) => section.events.length > 0);
}

interface MediationTimelinePanelProps {
	timeline: MediationTimelineEvent[];
	currency: string;
	canOpenRef: (ref: string) => boolean;
	onOpenRef: (ref: string) => void;
}

/** Chronologie unique du dossier, par phases, filtrable par quatre icônes. */
export function MediationTimelinePanel({ timeline, currency, canOpenRef, onOpenRef }: MediationTimelinePanelProps) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();
	const [filter, setFilter] = useState<Filter>("all");
	const sections = useMemo(() => buildSections(timeline), [timeline]);

	const visible = (event: MediationTimelineEvent) => filter === "all" || FILTER_KINDS[filter].includes(event.kind);
	const shownSections = sections.map((section) => ({ ...section, shown: section.events.filter(visible) })).filter((section) => section.shown.length > 0);

	const filterBar = (
		<div className="timeline-filters" role="group" aria-label={t("adminChatDetail.dossier.timeline.title")}>
			{FILTERS.map((option) => (
				<button
					key={option.value}
					type="button"
					className={`timeline-filter${filter === option.value ? " timeline-filter--active" : ""}`}
					aria-pressed={filter === option.value}
					aria-label={t(option.label)}
					title={t(option.label)}
					onClick={() => setFilter(option.value)}
				>
					<i className={`fas ${option.icon}`} aria-hidden="true"></i>
				</button>
			))}
		</div>
	);

	return (
		<CollapsibleCard className="mediation-timeline" id="mediation-timeline" title={t("adminChatDetail.dossier.timeline.title")} headerExtra={filterBar}>
			{shownSections.length === 0 ? (
				<p>{t("adminChatDetail.dossier.timeline.empty")}</p>
			) : (
				shownSections.map((section) => (
					<div key={section.key} className="timeline-section">
						<h3 className="timeline-section-title">
							{t(section.title.key, section.title.values)}
							<span className="mediation-chip">{t("adminChatDetail.dossier.timeline.events", { count: section.shown.length, plural: section.shown.length !== 1 ? "s" : "" })}</span>
						</h3>
						<ol className="mediation-timeline-list">
							{section.shown.map((event) => {
								const style = EVENT_STYLE[event.type];
								return (
									<li key={event.ref} className={`mediation-timeline-item mediation-timeline-item--${style.tone}`}>
										<span className="mediation-timeline-date">{new Date(event.at).toLocaleString()}</span>
										<span>
											<i className={`fas ${style.icon}`} aria-hidden="true"></i>{" "}
											<strong>{t(`adminChatDetail.dossier.timeline.type.${event.type}` as MessageKey)}</strong>
											{" · "}
											{t(`adminChatDetail.dossier.timeline.actor.${event.actor}` as MessageKey)}
											{event.roundNumber !== undefined && ` · ${t("adminChatDetail.dossier.timeline.round", { number: event.roundNumber })}`}
											{event.amount !== undefined && ` · ${formatCents(event.amount, event.currency ?? currency)}`}
											{event.fileCount ? ` · ${t("adminChatDetail.dossier.timeline.files", { count: event.fileCount, plural: event.fileCount !== 1 ? "s" : "" })}` : ""}
										</span>
										{canOpenRef(event.ref) && (
											<button type="button" className="btn btn-sm btn-outline mediation-open" onClick={() => onOpenRef(event.ref)}>
												{t("adminChatDetail.dossier.timeline.open")}
											</button>
										)}
									</li>
								);
							})}
						</ol>
					</div>
				))
			)}
		</CollapsibleCard>
	);
}
